import React from 'react';
import {
    Groups as TeamsIcon,
    Engineering as GearIcon,
    DesignServices as DesignIcon,
    PhoneAndroid as MobileIcon,
    BugReport as QualityIcon,
    Build as ToolsIcon,
    Flag as FlagIcon,
    People as PeopleIcon,
    LocationCity as BuildingIcon,
    Extension as PuzzleIcon,
    CheckCircle as CheckIcon,
    Schedule as ScheduleIcon,
    Language as LanguageIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Typography,
    useTheme,
    alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const OutSourcePage = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const services = [
        {
            icon: <GearIcon fontSize="large" />,
            title: "Web developers",
            description: "IT team Outsourcing with developers that have solid experience building web apps using modern frameworks, high-level programming languages, and other tools. Our talent pool is perfectly able to complete your software development team."
        },
        {
            icon: <DesignIcon fontSize="large" />,
            title: "Designers",
            description: "Professional middle-senior level UI/UX designers can make either a completed design or a single UI element for your software project. They stick to a strong work ethic and deliver required graphics for your software always on time!"
        },
        {
            icon: <MobileIcon fontSize="large" />,
            title: "Mobile developers",
            description: "Developers that work with iOS, Android, PWA, and Hybrid apps. They have expertise in all stages of mobile app development from choosing the right technological stack to deploying to stores."
        },
        {
            icon: <QualityIcon fontSize="large" />,
            title: "QA and testing engineers",
            description: "Top-talent QAs that provide functional testing (security, integration, acceptance), non-functional testing (performance, usability, etc.), and regression testing for software development."
        }
    ];

    const stats = [
        { value: "70%", label: "Returning clients" },
        { value: "98%", label: "Satisfaction rate" },
        { value: "10+", label: "Years experience" },
        { value: "150+", label: "Successful projects" }
    ];

    const benefits = [
        { icon: <FlagIcon />, title: "Expertise", description: "70% of our experts are top-talent senior developers" },
        { icon: <PeopleIcon />, title: "On-demand scaling", description: "Scale up and down the number of developers on demand" },
        { icon: <BuildingIcon />, title: "Attention to details", description: "We build the perfect team depending on your needs" },
        { icon: <PuzzleIcon />, title: "In-house experience", description: "Our experts adhere to your existing processes" }
    ];

    const criteria = [
        { icon: <CheckIcon />, title: "Relevant expertise", description: "Look for actual experience in your specific industry" },
        { icon: <ScheduleIcon />, title: "Established processes", description: "Well-organized workflows and meeting deadlines" },
        { icon: <QualityIcon />, title: "Data privacy", description: "NDA and security must be integral parts of service" },
        { icon: <TeamsIcon />, title: "Talent pool", description: "Transparent information about their developers' sources" },
        { icon: <ScheduleIcon />, title: "Time zone overlap", description: "Convenient working hours for collaboration" },
        { icon: <LanguageIcon />, title: "English proficiency", description: "All team members should communicate clearly" }
    ];

    return (
        <Box sx={{ bgcolor: 'background.default' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: 'rgba(63, 81, 181, 0.9)',
                    color: 'primary.contrastText',
                    py: { xs: 10, md: 14 },
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)} 0%, ${alpha(theme.palette.primary.light, 0.25)} 100%)`,
                        zIndex: 1
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Stack alignItems="center" textAlign="center" spacing={5}>
                        <Chip
                        onClick={()=>console.log("")}
                            icon={<TeamsIcon />}
                            label="IT Staff Outsourcing Services"
                            sx={{
                                backdropFilter: 'blur(10px)',
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                color: 'primary.contrastText',
                                px: 2,
                                py: 1,
                                fontWeight: 500,
                                fontSize: '0.875rem'
                            }}
                        />

                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '2.25rem', md: '3.5rem' },
                                lineHeight: 1.2
                            }}
                        >
                            Ready-to-go{" "}
                            <Box
                                component="span"
                                sx={{
                                    background: 'linear-gradient(to right, #ffeb3b, #ffc107)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontWeight: 900
                                }}
                            >
                                Tech Teams
                            </Box>
                        </Typography>

                        <Typography variant="h6" sx={{ maxWidth: 780, opacity: 0.85 }}>
                            A custom-hired team of developers, designers, scrum masters & QA professionals—ready to scale your projects.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} pt={1}>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.75,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    boxShadow: 3,
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: 6
                                    }
                                }}
                            >
                                Build Your Team
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.75,
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    borderColor: alpha(theme.palette.common.white, 0.3),
                                    color: 'primary.contrastText',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: theme.palette.common.white,
                                        bgcolor: alpha(theme.palette.common.white, 0.1)
                                    }
                                }}
                            >
                                Meet Our Talent
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>


            {/* Services Section */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Stack spacing={6} alignItems="center" textAlign="center">
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                        IT Staff Outsourcing Services
                    </Typography>

                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
                        Our deep talent pool, proficient in a wide range of tech stacks, is ideally suited to meet your specific needs
                    </Typography>

                    <Grid container spacing={4}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Card elevation={2} sx={{ height: '100%', p: 4, textAlign: 'left' }}>
                                    <Stack direction="row" spacing={3} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                bgcolor: 'primary.light',
                                                color: 'primary.contrastText',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            {service.icon}
                                        </Box>
                                        <Box>
                                            <Typography variant="h5" component="h3" gutterBottom>
                                                {service.title}
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                                {service.description}
                                            </Typography>
                                            <Button
                                                variant="text"
                                                color="primary"
                                                endIcon={<span>&rarr;</span>}
                                                sx={{ px: 0 }}
                                            >
                                                Learn more
                                            </Button>
                                        </Box>
                                    </Stack>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Container>

            {/* Stats Section */}
            <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
                <Container maxWidth="lg">
                    <Stack spacing={6} alignItems="center" textAlign="center">
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                            IT Staff Outsourcing in Facts and Figures
                        </Typography>

                        <Grid container spacing={4} justifyContent="center">
                            {stats.map((stat, index) => (
                                <Grid item xs={6} sm={3} key={index}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'transparent' }}>
                                        <Typography variant="h2" component="div" color="primary" sx={{ fontWeight: 700 }}>
                                            {stat.value}
                                        </Typography>
                                        <Typography variant="h6" component="div">
                                            {stat.label}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800 }}>
                            We focus on long-term cooperation with 98% client satisfaction rate. Your data stays safe with our security guarantees and no communication barriers with English-proficient teams.
                        </Typography>
                    </Stack>
                </Container>
            </Box>


            <Box sx={{ bgcolor: 'grey.60', py: 10 }}>
                <Stack spacing={6} alignItems="center" textAlign="center">
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 800,
                            color: 'primary.main',
                            fontSize: { xs: '2rem', md: '2.75rem' },
                        }}
                    >
                        What You Will Get
                    </Typography>

                    <Grid container spacing={4} justifyContent="center">
                        {benefits.map((benefit, index) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={index}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 4,
                                        width: '100%',
                                        maxWidth: 280, // Ensures same width
                                        minHeight: 260,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        borderRadius: 3,
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: theme => theme.shadows[6],
                                            bgcolor: theme => alpha(theme.palette.primary.light, 0.05),
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 32,
                                            mb: 2,
                                        }}
                                    >
                                        {benefit.icon}
                                    </Box>

                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                                        {benefit.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {benefit.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Box>

            {/* Criteria Section */}
            <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
                <Container maxWidth="lg">
                    <Stack spacing={6} alignItems="center" textAlign="center">
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                            How To Choose a Reliable Partner
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700 }}>
                            A dependable partner brings strategic value to your business. Here’s what to look for:
                        </Typography>
                    </Stack>

                    <Grid container spacing={4} justifyContent="center" sx={{ mt: 6 }}>
                        {criteria.map((item, index) => (
                            <Grid item key={index}>
                                <Box
                                    sx={{
                                        width: 500,
                                        p: 4,
                                        height: '100%',
                                        bgcolor: 'white',
                                        borderRadius: 4,
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                                        transition: 'all 0.3s ease',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 2,
                                        '&:hover': {
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                            transform: 'translateY(-4px)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 24,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>



            {/* CTA Section */}
            <Box sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                py: 10,
                backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
            }}>
                <Container maxWidth="lg">
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Typography variant="h3" component="h2" sx={{ fontWeight: 700 }}>
                            Ready to Augment Your Team?
                        </Typography>

                        <Typography variant="h6" sx={{ maxWidth: 700, opacity: 0.9 }}>
                            Whether you need one specialist or a complete development team, we have the right talent for your project.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate('/get-in-touch')}
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem'
                                }}
                            >
                                Get Started
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    borderColor: alpha(theme.palette.common.white, 0.3),
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        borderColor: theme.palette.common.white
                                    }
                                }}
                            >
                                Contact Sales
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default OutSourcePage;